import { cn } from '@utils/cn';

import { Chip, Icon, Text } from '@ui-component-library';

import { useCheckout } from '@components/Checkout/context/CheckoutContext';

const getPackageText = (p) => {
  if (p.id === 0) {
    return {
      id: 0,
      title: 'Bas',
      subtitle: 'Skadeanalys: Barkborre',
      description: 'Utöver högupplösta bilder av skogen får du en analys av borkborreskador',
      price: p.price_ha,
      internalName: p.internal_name,
      minimumOrderAmount: p.minimum_order_amount
    };
  }

  if (p.id === 1) {
    return {
      id: 1,
      title: 'Plus',
      subtitle: 'Skadeanalys: Barkborre och toppbrottsanalys',
      description:
        'Identifiera både barkborreangrepp och toppbrott för att agera i tid, undvika spridning och rädda virkesvärden',
      price: p.price_ha,
      internalName: p.internal_name,
      minimumOrderAmount: p.minimum_order_amount
    };
  }

  return;
};
const Card = ({ id, title, subtitle, description, price, isSelected, onClick }) => {
  const textPrimaryColor = isSelected ? 'text-white-50' : 'text-charcoal-500';
  const textSecondaryColorPremium = isSelected ? 'text-purple-plum-200' : 'text-purple-plum-500';
  const textSecondaryColorBase = isSelected ? 'text-cloud-500' : 'text-cloud-900';
  const chipClasses = isSelected
    ? 'bg-purple-plum-500 fill-white-50 text-white-50'
    : 'bg-purple-plum-500/8 fill-purple-plum-500 text-purple-plum-500';
  const bgColor = isSelected ? 'bg-charcoal-500' : 'bg-white-50';

  const IconColor = isSelected
    ? 'fill-spring-yellow-500 text-spring-yellow-500'
    : 'fill-purple-plum-500 text-purple-plum-500';

  const hectarTextColor = isSelected ? 'text-white-600' : 'text-pebble-500';

  return (
    <div
      className={`hover:border-pebble-500 flex w-full cursor-pointer flex-row justify-between gap-2 rounded-xl border border-transparent p-4 ${bgColor}`}
      onClick={onClick}
    >
      <div className="flex flex-col">
        <div className="flex flex-row items-baseline gap-1 pb-1">
          <Text variant="text-base/leading-normal/medium" className={textPrimaryColor}>
            Skadeanalys
          </Text>
          <Text
            variant="text-base/leading-normal/medium"
            className={title === 'Plus' ? textSecondaryColorPremium : textSecondaryColorBase}
          >
            {title}
          </Text>
        </div>
        <Text variant="text-sm/leading-normal/normal" className={cn('pb-3', textPrimaryColor)}>
          {subtitle}
        </Text>

        <Text variant="text-sm/leading-normal/normal" className={cn('pb-4', textPrimaryColor)}>
          {description}
        </Text>
        <div
          className={cn(
            'flex flex-row items-center gap-2',
            isSelected || title === 'Plus' ? 'opacity-100' : 'opacity-0'
          )}
        >
          <Icon
            name={isSelected ? 'CheckFilled' : 'SparkleSimple'}
            size="large"
            className={cn(isSelected ? 'fill-spring-yellow-500' : 'fill-purple-plum-500')}
          />
          <Text variant="text-xs/leading-normal/medium" className={IconColor}>
            {isSelected ? 'Tillagd' : title === 'Plus' ? 'Rekommenderad' : ''}
          </Text>
        </div>
      </div>

      <div className="flex flex-col items-end justify-between self-stretch">
        <Chip label="Tillägg" icon="PlusSimple" className={chipClasses} />

        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-row items-baseline gap-1">
            <Text variant="text-xl/leading-normal/medium" className={textPrimaryColor}>
              {price}
            </Text>
            <Text variant="text-sm/leading-normal/normal" className={textPrimaryColor}>
              SEK
            </Text>
          </div>
          <Text variant="text-xs/leading-normal/normal" className={hectarTextColor}>
            per hektar
          </Text>
        </div>
      </div>
    </div>
  );
};
export const DroneInfo = () => {
  const { orderPrices, extraData, setExtraData } = useCheckout();

  const dronePrices = extraData.dronePrices;
  const selectedPackage = extraData.selectedPackage;

  const packages =
    dronePrices?.packages
      ?.map((p) => getPackageText(p))
      .filter((p): p is NonNullable<typeof p> => p !== null) ?? [];

  const handlePackageSelect = (p) => {
    if (selectedPackage?.id === p.id) {
      setExtraData((prev) => ({ ...prev, selectedPackage: null }));
      return;
    }

    setExtraData((prev) => ({ ...prev, selectedPackage: p }));
  };
  return (
    <div>
      <div className="flex w-full flex-col gap-8">
        <div className="flex flex-col gap-2 pl-4">
          <Text variant="text-base/leading-normal/medium" className="text-charcoal-500">
            Alla paket börjar med ortofoto.
          </Text>
          <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
            För att utöka analysen kan ett av tilläggen väljas till.
          </Text>
        </div>
        <div className="bg-cloud-300 flex w-full flex-row justify-between gap-2 rounded-xl border border-transparent p-4">
          <div className="flex flex-col">
            <div className="flex flex-row items-baseline gap-1 pb-1">
              <Text variant="text-base/leading-normal/semibold" className="text-charcoal-500">
                Ortofoto
              </Text>
              <Text variant="text-base/leading-normal/semibold" className="text-pebble-500">
                Drönaranalys
              </Text>
            </div>
            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-500 pb-3">
              Högupplösta bilder & skadeanalys
            </Text>

            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-500 pb-4">
              Exakt överblick över din skog -direkt i din skogsbruksplan.
            </Text>
          </div>

          <div className="flex flex-col items-end justify-between self-stretch">
            <Chip
              label="Ingår"
              icon="CheckSimple"
              className="bg-charcoal-500 text-spring-yellow-500 fill-spring-yellow-500"
            />

            {orderPrices?.propertiesHectarePrice && (
              <div className="flex flex-col items-end gap-1">
                <div className="flex flex-row items-baseline gap-1">
                  <Text variant="text-xl/leading-normal/medium" className="text-charcoal-500">
                    {Number(orderPrices.propertiesHectarePrice)}
                  </Text>
                  <Text variant="text-sm/leading-normal/normal" className="text-charcoal-500">
                    SEK
                  </Text>
                </div>
                <Text variant="text-xs/leading-normal/normal" className="text-pebble-500">
                  per hektar
                </Text>
              </div>
            )}
          </div>
        </div>
        <div className="h-[0.5px] w-full border border-stone-500/17" />
        <Text variant="text-base/leading-normal/medium" className="pl-4">
          Välj extra tjänster
        </Text>
        <div className="flex flex-col gap-4">
          {packages.map((p) => (
            <Card
              key={p.title}
              {...p}
              isSelected={selectedPackage?.id === p.id}
              onClick={() => handlePackageSelect(p)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
